import {
  getClientBuildCommit,
  isBuildMismatch,
  syncClientBuildCommitMarker,
} from "./buildIdentity";

/** sessionStorage key — one automatic reload per browser tab session / build. */
export const CHUNK_RELOAD_SESSION_KEY = "clarity-portal:chunk-reload-attempted";

const CHUNK_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /importing a module script failed/i,
  /error loading dynamically imported module/i,
  /loading chunk [\w-]+ failed/i,
  /chunkloaderror/i,
  /failed to load module script/i,
  /mime type .+text\/html/i,
  /expected a javascript.*module script/i,
  /responded with a mime type of ["']?text\/html/i,
];

export type NormalizedRuntimeError = {
  name: string;
  message: string;
  stack: string;
  failedChunkUrl: string | null;
  raw: unknown;
};

function extractUrlCandidate(text: string): string | null {
  const match = text.match(/https?:\/\/[^\s)'"]+|\/assets\/[^\s)'"]+/i);
  return match?.[0] ?? null;
}

export function normalizeRuntimeError(error: unknown): NormalizedRuntimeError {
  let name = "Error";
  let message = "";
  let stack = "";
  let failedChunkUrl: string | null = null;

  if (error instanceof Error) {
    name = error.name || "Error";
    message = error.message || "";
    stack = error.stack || "";
    failedChunkUrl = extractUrlCandidate(`${message} ${stack}`);
  } else if (typeof error === "string") {
    message = error;
    failedChunkUrl = extractUrlCandidate(error);
  } else if (typeof ErrorEvent !== "undefined" && error instanceof ErrorEvent) {
    name = error.error instanceof Error ? error.error.name : "ErrorEvent";
    message =
      (error.error instanceof Error ? error.error.message : "") ||
      error.message ||
      "";
    stack = error.error instanceof Error ? error.error.stack || "" : "";
    if (error.filename) failedChunkUrl = error.filename;
    const target = error.target;
    if (!failedChunkUrl && target instanceof HTMLScriptElement && target.src) {
      failedChunkUrl = target.src;
    }
    if (!failedChunkUrl && target instanceof HTMLLinkElement && target.href) {
      failedChunkUrl = target.href;
    }
  } else if (typeof error === "object" && error !== null) {
    const record = error as {
      name?: unknown;
      message?: unknown;
      stack?: unknown;
      reason?: unknown;
      target?: EventTarget | null;
      filename?: unknown;
    };
    if (typeof record.name === "string") name = record.name;
    if (typeof record.message === "string") message = record.message;
    if (typeof record.stack === "string") stack = record.stack;
    if (typeof record.filename === "string") failedChunkUrl = record.filename;
    if (record.target instanceof HTMLScriptElement && record.target.src) {
      failedChunkUrl = record.target.src;
      if (!message) message = `Script load failed: ${record.target.src}`;
    }
    if (record.target instanceof HTMLLinkElement && record.target.href) {
      failedChunkUrl = record.target.href;
      if (!message) message = `Stylesheet load failed: ${record.target.href}`;
    }
    if (!message && record.reason != null) {
      const nested = normalizeRuntimeError(record.reason);
      name = nested.name;
      message = nested.message;
      stack = nested.stack;
      failedChunkUrl = nested.failedChunkUrl ?? failedChunkUrl;
    }
  }

  if (!failedChunkUrl) {
    failedChunkUrl = extractUrlCandidate(`${message} ${stack}`);
  }

  return {
    name,
    message,
    stack,
    failedChunkUrl,
    raw: error,
  };
}

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const normalized = normalizeRuntimeError(error);
  const haystack = `${normalized.name} ${normalized.message} ${normalized.stack} ${normalized.failedChunkUrl ?? ""}`;

  if (CHUNK_ERROR_PATTERNS.some((re) => re.test(haystack))) {
    return true;
  }

  // Failed fetch / Safari "Load failed" of a hashed /assets/* URL (common after deploy).
  // Bare "Load failed" without an asset URL is intentionally ignored — Safari uses that
  // for many network failures and must not trigger deploy recovery (Scenario D).
  if (
    normalized.failedChunkUrl &&
    /\/assets\//i.test(normalized.failedChunkUrl) &&
    /\.(js|mjs|css)(\?|$)/i.test(normalized.failedChunkUrl) &&
    /(failed to fetch|load failed|networkerror|loading|importing|module)/i.test(haystack)
  ) {
    return true;
  }

  // Safari / Chromium script/link load events with asset src
  if (normalized.failedChunkUrl && /\/assets\/.+\.(js|mjs|css)(\?|$)/i.test(normalized.failedChunkUrl)) {
    if (!normalized.message || /load failed|script|module|css|failed/i.test(normalized.message)) {
      return true;
    }
  }

  return false;
}

export function hasAttemptedChunkReload(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

export function markChunkReloadAttempted(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, "1");
  } catch {
    // Ignore private browsing quota errors.
  }
}

export function clearChunkReloadAttempted(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

/** Call once on app boot so a successful reload onto a new build resets the one-shot flag. */
export function initChunkLoadRecoveryForBuild(): void {
  syncClientBuildCommitMarker(clearChunkReloadAttempted);
}

/**
 * Recover from stale Vite chunks after deployment.
 * Reloads once per tab session; returns true when a reload was triggered.
 */
export function attemptChunkLoadRecovery(error: unknown): boolean {
  if (typeof window === "undefined") return false;
  if (!isChunkLoadError(error)) return false;
  if (hasAttemptedChunkReload()) return false;

  markChunkReloadAttempted();
  window.location.reload();
  return true;
}

export function createIncidentId(now = Date.now()): string {
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  const time = now.toString(36).toUpperCase().slice(-4);
  return `${time}-${rand}`;
}

export type RuntimeErrorDiagnostics = {
  incidentId: string;
  timestamp: string;
  currentRoute: string;
  previousRoute: string | null;
  browserBuildId: string;
  serverBuildId: string | null;
  failedChunkUrl: string | null;
  errorName: string;
  message: string;
  stack: string;
  browser: string;
  online: boolean;
  isChunkLoadFailure: boolean;
  buildMismatch: boolean;
};

let lastSeenRoute: string | null = null;
let previousRouteMemory: string | null = null;

/** Call on client navigations so diagnostics can include previousRoute. */
export function rememberRouteForDiagnostics(pathname?: string): void {
  if (typeof window === "undefined") return;
  const route =
    pathname ?? `${window.location.pathname}${window.location.search}`;
  if (lastSeenRoute && lastSeenRoute !== route) {
    previousRouteMemory = lastSeenRoute;
  }
  lastSeenRoute = route;
}

export function buildRuntimeErrorDiagnostics(
  error: unknown,
  options?: {
    incidentId?: string;
    serverBuildId?: string | null;
    previousRoute?: string | null;
  },
): RuntimeErrorDiagnostics {
  const normalized = normalizeRuntimeError(error);
  const currentRoute =
    typeof window !== "undefined" ? window.location.pathname + window.location.search : "(ssr)";
  const browserBuildId = getClientBuildCommit();
  const serverBuildId = options?.serverBuildId ?? null;
  return {
    incidentId: options?.incidentId ?? createIncidentId(),
    timestamp: new Date().toISOString(),
    currentRoute,
    previousRoute: options?.previousRoute ?? previousRouteMemory,
    browserBuildId,
    serverBuildId,
    failedChunkUrl: normalized.failedChunkUrl,
    errorName: normalized.name,
    message: normalized.message || "(no message)",
    stack: normalized.stack || "",
    browser: typeof navigator !== "undefined" ? navigator.userAgent : "(unknown)",
    online: typeof navigator !== "undefined" ? navigator.onLine : true,
    isChunkLoadFailure: isChunkLoadError(error),
    buildMismatch: isBuildMismatch(browserBuildId, serverBuildId),
  };
}

export function logRuntimeErrorDiagnostics(diagnostics: RuntimeErrorDiagnostics): void {
  console.error("[PortalRuntimeError]", diagnostics);
}

export const CHUNK_RELOAD_USER_MESSAGE = "The Portal has been updated.";

export const CHUNK_RELOAD_IN_PROGRESS_MESSAGE = "Reloading to the latest version…";

export const CHUNK_RELOAD_MANUAL_MESSAGE = "Please reload the page.";
